import React, { useRef, useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { X, Minus, Plus, ShoppingCart, Bitcoin } from 'lucide-react';
import { formatCurrency, formatBitcoin } from '../utils/formatters';

interface CartProps {
  bitcoinPrice: number;
}

export const Cart: React.FC<CartProps> = ({ bitcoinPrice }) => {
  const { state, removeItem, updateQuantity, toggleCart, clearCart, getTotalPrice } = useCart();
  const totalBitcoin = bitcoinPrice ? getTotalPrice() / bitcoinPrice : 0;
  const cartRef = useRef<HTMLDivElement>(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const handleClose = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsAnimatingOut(false);
      toggleCart();
    }, 300); // Match this with CSS transition duration
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (state.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.isOpen, toggleCart]);

  if (!state.isOpen && !isAnimatingOut) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isAnimatingOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      
      {/* Cart Panel */}
      <div 
        ref={cartRef}
        className={`fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isAnimatingOut ? 'translate-x-full' : 'translate-x-0'
        }`}
        style={{
          willChange: 'transform',
        }}
      >
        {/* Cart Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Your Cart</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="overflow-y-auto h-[calc(100vh-240px)]">
          {state.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <ShoppingCart className="w-16 h-16 mb-4" />
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {state.items.map((item) => (
                <div
                  key={item.name}
                  className="flex gap-4 bg-white rounded-lg p-4 shadow-sm border border-gray-100"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-semibold">{item.name}</h3>
                      <button
                        onClick={() => removeItem(item.name)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-gray-500 text-sm">{formatCurrency(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.name, Math.max(0, item.quantity - 1))}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.name, item.quantity + 1)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Footer */}
        {state.items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total:</span>
              <div className="text-right">
                <div className="font-bold text-xl">{formatCurrency(getTotalPrice())}</div>
                <div className="flex items-center gap-1 text-orange-500">
                  <Bitcoin className="w-4 h-4" />
                  <span className="font-medium">{formatBitcoin(totalBitcoin)} BTC</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={clearCart}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Cart
              </button>
              <button
                onClick={() => {
                  // Handle checkout
                  alert('Checkout functionality coming soon!');
                }}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 